import {
  DocumentParser,
  ParsedDocument,
  RawSymbol,
  ScanContext,
  ScanResult,
  SignatureVerifier,
} from './types';
import { ImagePixels, QrDecoder } from './ports';

/**
 * Holds the registered parsers + signature verifiers and runs the scan
 * pipeline. Adding a document type is `registry.register(new MyParser())`;
 * adding signature support is `registry.registerVerifier(new MyVerifier())`.
 */
export class DocumentRegistry {
  private readonly parsers: DocumentParser[] = [];
  private readonly verifiers: SignatureVerifier[] = [];

  constructor(private readonly decoder: QrDecoder) {}

  register(...parsers: DocumentParser[]): this {
    this.parsers.push(...parsers);
    return this;
  }

  registerVerifier(...verifiers: SignatureVerifier[]): this {
    this.verifiers.push(...verifiers);
    return this;
  }

  get registered(): readonly DocumentParser[] {
    return this.parsers;
  }

  /** Runs registered parsers, then verifies the signature if a verifier matches. */
  async parseContext(ctx: ScanContext): Promise<ParsedDocument | null> {
    for (const parser of this.parsers) {
      let matched = false;
      try {
        matched = parser.detect(ctx);
      } catch {
        matched = false;
      }
      if (matched) {
        const doc = await parser.parse(ctx);
        return this.verifySignature(doc);
      }
    }
    return null;
  }

  /** Re-run signature verification on an already-parsed document. */
  verifyDocument(doc: ParsedDocument): Promise<ParsedDocument> {
    return this.verifySignature(doc);
  }

  private async verifySignature(doc: ParsedDocument): Promise<ParsedDocument> {
    if (!doc.signature) {
      return doc;
    }
    for (const verifier of this.verifiers) {
      if (verifier.supports(doc)) {
        try {
          doc.signature = await verifier.verify(doc);
        } catch (e) {
          doc.signature = { ...doc.signature, status: 'unverified', detail: String(e) };
        }
        break;
      }
    }
    return doc;
  }

  /** Decodes symbols from pixels, then parses. Optionally merges extra MRZ text. */
  async scan(image: ImagePixels, opts: { mrzText?: string } = {}): Promise<ScanResult> {
    let symbols: RawSymbol[] = [];
    try {
      symbols = await this.decoder.decode(image);
    } catch {
      symbols = [];
    }
    const ctx: ScanContext = { symbols, mrzText: opts.mrzText };
    const document = await this.parseContext(ctx);
    return { document, symbols };
  }

  /** Parse from already-decoded symbols and/or MRZ text (no image needed). */
  async scanContext(ctx: ScanContext): Promise<ScanResult> {
    return { document: await this.parseContext(ctx), symbols: ctx.symbols };
  }
}
